import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CircleAlert,
  Copy,
  CornerUpLeft,
  Download,
  EyeOff,
  File as FileIcon,
  FileArchive,
  FileText,
  Flag,
  Hash,
  Link,
  Megaphone,
  MessagesSquare,
  Pencil,
  Phone,
  PhoneOff,
  Pin,
  PinOff,
  Reply,
  RotateCcw,
  SmilePlus,
  Trash2,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { Permission, type Attachment, type LinkEmbed } from '@spawnwire/shared';
import { Avatar, clsx, ContextMenu, IconButton, TextArea, Tooltip, UserBadge, type MenuEntry } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt, retryPending, toggleReaction } from '../../lib/data';
import { QUICK_REACTIONS, replaceShortcodes } from '../../lib/emoji';
import { copyText } from '../../lib/hooks';
import { isEmojiOnly, Markdown, plainText, type RenderContext } from '../../lib/markdown';
import { decodeMentions, encodeMentions } from '../../lib/mentionText';
import { NameText } from '../../lib/nameFonts';
import { SpaceTagBadge } from '../members/SpaceTag';
import { channelPath, navigate } from '../../lib/nav';
import { can } from '../../lib/perms';
import { publicOrigin } from '../../lib/platform';
import { formatBytes, formatFull, formatTime, formatTimestamp } from '../../lib/time';
import { actions, displayNameIn, roleColorOf, useStore, type UiMessage } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { EmojiPicker } from './EmojiPicker';
import { MessageComponents } from './MessageComponents';
import { appMenuEntries, useSpaceCommands } from './slashCommands';
import { RichEmbedView } from './RichEmbed';

export function useRenderContext(spaceId: string | null): RenderContext {
  const me = useStore((s) => s.me?.id);
  return {
    userLabel: (id) => displayNameIn(useStore.getState(), id, spaceId),
    roleLabel: (id) => {
      const role = spaceId ? useStore.getState().spaces[spaceId]?.roles.find((r) => r.id === id) : undefined;
      return role ? { name: role.name, color: role.color } : null;
    },
    channelLabel: (id) => useStore.getState().channels[id]?.name ?? null,
    onUser: (id, el) => ui.openProfile(id, spaceId, el),
    onChannel: (id) => {
      const c = useStore.getState().channels[id];
      if (c) navigate(channelPath(c));
    },
    isSelf: (id) => id === me,
  };
}

function AttachmentView({ a }: { a: Attachment }) {
  const image = a.contentType.startsWith('image/');
  const video = a.contentType.startsWith('video/') && ['video/mp4', 'video/webm'].includes(a.contentType);
  const audio = a.contentType.startsWith('audio/');
  if (image) {
    const maxW = 400;
    const maxH = 320;
    const ratio = a.width && a.height ? Math.min(1, maxW / a.width, maxH / a.height) : 1;
    const w = a.width ? Math.round(a.width * ratio) : undefined;
    const h = a.height ? Math.round(a.height * ratio) : undefined;
    return (
      <button type="button" className="block max-w-full overflow-hidden rounded-lg border border-line bg-active" onClick={() => ui.open({ kind: 'lightbox', url: a.url, filename: a.filename })} aria-label={`Open image ${a.filename}`}>
        {/* A smaller copy loads much faster; the lightbox shows the original. */}
        <img src={`${a.url}?preview=1`} alt={a.filename} loading="lazy" width={w} height={h} className="block max-h-80 max-w-full object-contain" style={w ? { width: w } : undefined} />
      </button>
    );
  }
  if (video) {
    return <video src={a.url} controls preload="metadata" className="max-h-80 max-w-full rounded-lg border border-line bg-black" aria-label={a.filename} />;
  }
  const Icon = a.contentType.includes('zip') || a.contentType.includes('tar') || a.contentType.includes('7z') || a.contentType.includes('gzip') ? FileArchive : a.contentType.startsWith('text/') || a.contentType.includes('pdf') ? FileText : FileIcon;
  return (
    <div className="flex w-full max-w-md items-center gap-3 rounded-lg border border-line bg-sidebar p-3">
      <Icon className="size-8 shrink-0 text-accent" aria-hidden />
      <div className="min-w-0 flex-1">
        <a href={`${a.url}?download=1`} className="block truncate text-sm font-medium text-link hover:underline" download={a.filename}>
          {a.filename}
        </a>
        <div className="text-xs text-faint">{formatBytes(a.size)}</div>
        {audio && <audio src={a.url} controls preload="none" className="mt-2 w-full" />}
      </div>
      <a href={`${a.url}?download=1`} download={a.filename} aria-label={`Download ${a.filename}`} className="rounded p-1.5 text-muted hover:bg-hover hover:text-fg">
        <Download className="size-5" />
      </a>
    </div>
  );
}

function EmbedView({ e }: { e: LinkEmbed }) {
  return (
    <div className="max-w-md rounded-md border-l-4 border-accent bg-sidebar px-3 py-2">
      {e.siteName && <div className="text-xs text-faint">{e.siteName}</div>}
      {e.title && (
        <a href={e.url} target="_blank" rel="noopener noreferrer nofollow ugc" className="mt-0.5 block font-semibold text-link hover:underline">
          {e.title}
        </a>
      )}
      {e.description && <p className="mt-1 line-clamp-3 text-sm text-muted">{e.description}</p>}
    </div>
  );
}

function SystemLine({ icon: Icon, children, time }: { icon: typeof Hash; children: ReactNode; time: string }) {
  return (
    <div className="group flex items-center gap-3 px-4 py-1 text-sm text-muted hover:bg-hover/50">
      <span className="flex w-10 shrink-0 justify-center">
        <Icon className="size-4 text-faint" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">{children}</span>
      <Tooltip label={formatFull(time)}>
        <time className="shrink-0 text-xs text-faint" dateTime={time}>
          {formatTimestamp(time)}
        </time>
      </Tooltip>
    </div>
  );
}

function EditBox({ message, onDone }: { message: UiMessage; onDone: () => void }) {
  const [value, setValue] = useState(() => decodeMentions(message.content, useStore.getState()));
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  async function save() {
    const content = encodeMentions(replaceShortcodes(value.trim()), useStore.getState(), message.channelId);
    if (content === message.content) return onDone();
    if (!content && message.attachments.length === 0) {
      onDone();
      ui.confirm({ title: 'Delete message?', body: 'An empty message will be deleted.', confirmLabel: 'Delete', danger: true, onConfirm: () => http.del(`/channels/${message.channelId}/messages/${message.id}`) });
      return;
    }
    const updated = await attempt(() => http.patch<UiMessage>(`/channels/${message.channelId}/messages/${message.id}`, { content }));
    if (updated) actions.updateMessage(updated);
    onDone();
  }
  return (
    <div className="mt-1">
      <TextArea
        ref={ref}
        value={value}
        rows={Math.min(10, value.split('\n').length + 1)}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onDone();
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
        }}
        aria-label="Edit message"
      />
      <p className="mt-1 text-xs text-muted">
        Escape to <button type="button" className="text-accent hover:underline" onClick={onDone}>cancel</button>, Enter to{' '}
        <button type="button" className="text-accent hover:underline" onClick={() => void save()}>save</button>
      </p>
    </div>
  );
}

export interface MessageItemProps {
  message: UiMessage;
  grouped: boolean;
  spaceId: string | null;
  perms: number;
  highlighted?: boolean;
  canThread: boolean;
  isPrivate: boolean;
  /** In an announcement channel: messages can be published, and published ones stand out. */
  announcement?: boolean;
}

export const MessageItem = memo(function MessageItem({ message, grouped, spaceId, perms, highlighted, canThread, isPrivate, announcement }: MessageItemProps) {
  const me = useStore((s) => s.me);
  const author = useStore((s) => (message.author ? (s.users[message.author.id] ?? message.author) : null));
  const blocked = useStore((s) => (message.author ? s.relationships[message.author.id]?.type === 'blocked' : false));
  const showBlocked = useStore((s) => s.me?.settings.showBlockedMessages ?? false);
  const showEmbeds = useStore((s) => s.me?.settings.showEmbeds ?? true);
  // The menu offers bots' message commands, which load after the first messages are drawn.
  useSpaceCommands(spaceId);
  const compact = useStore((s) => s.me?.settings.density === 'compact');
  const roleColor = useStore((s) => (message.author ? roleColorOf(s, spaceId, message.author.id) : null));
  const name = useStore((s) => (message.author ? displayNameIn(s, message.author.id, spaceId) : 'Deleted User'));
  const styledName = (
    <>
      <NameText font={author?.nameFont}>{name}</NameText>
      <UserBadge user={author} />
    </>
  );
  const thread = useStore((s) => (message.thread ? (s.channels[message.thread.id] ?? null) : null));
  const editing = useUi((s) => s.editing === message.id);
  const ctx = useRenderContext(spaceId);
  const [revealBlocked, setRevealBlocked] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const own = !!me && message.author?.id === me.id;
  const pending = message.status === 'sending';
  const failed = message.status === 'failed';
  const realId = !message.id.startsWith('pending:');
  const canManage = !isPrivate && can(perms, Permission.MANAGE_MESSAGES);
  const canPin = isPrivate || canManage;
  const canReact = can(perms, Permission.ADD_REACTIONS);
  const canPublish =
    !!announcement && realId && !message.publishedAt && (message.type === 'default' || message.type === 'reply') && ((own && can(perms, Permission.SEND_MESSAGES)) || canManage);
  const mentioned =
    !!me &&
    (message.mentions.includes(me.id) ||
      message.mentionEveryone ||
      (spaceId ? message.mentionRoles.some((r) => useStore.getState().myMembers[spaceId]?.roleIds.includes(r)) : false));

  // ---- System messages -----------------------------------------------------------
  const who = <button type="button" className="font-medium text-fg hover:underline" onClick={(e) => author && ui.openProfile(author.id, spaceId, e.currentTarget)}>{styledName}</button>;
  switch (message.type) {
    case 'member_join':
      return <SystemLine icon={UserPlus} time={message.createdAt}>{who} joined the space. Say hi!</SystemLine>;
    case 'pin':
      return (
        <SystemLine icon={Pin} time={message.createdAt}>
          {who} pinned{' '}
          <button type="button" className="font-medium text-fg hover:underline" onClick={() => message.replyTo && ui.jumpTo(message.channelId, message.replyTo.id)}>
            a message
          </button>{' '}
          to this channel.
        </SystemLine>
      );
    case 'thread_created':
      return (
        <SystemLine icon={MessagesSquare} time={message.createdAt}>
          {who} started a thread: <span className="font-medium text-fg">{message.content}</span>
        </SystemLine>
      );
    case 'call':
      return <SystemLine icon={Phone} time={message.createdAt}>{who} started a call.</SystemLine>;
    case 'call_timeout': {
      const minutes = Number(message.content) || 3;
      const span = `${minutes} minute${minutes === 1 ? '' : 's'}`;
      return (
        <SystemLine icon={PhoneOff} time={message.createdAt}>
          {own ? (
            <>You were alone in this call for over {span}, so we ended it. Ending empty calls saves our server resources and keeps Spawnwire completely free.</>
          ) : (
            <>{who}'s call ended after {span} with no one else in it.</>
          )}
        </SystemLine>
      );
    }
    case 'recipient_add':
      return (
        <SystemLine icon={UserPlus} time={message.createdAt}>
          {who} added <Markdown content={message.content} ctx={ctx} /> to the group.
        </SystemLine>
      );
    case 'recipient_remove':
      return (
        <SystemLine icon={UserMinus} time={message.createdAt}>
          <Markdown content={message.content} ctx={ctx} /> left or was removed from the group.
        </SystemLine>
      );
    case 'channel_name_change':
      return (
        <SystemLine icon={Pencil} time={message.createdAt}>
          {who} renamed the conversation to <span className="font-medium text-fg">{message.content}</span>.
        </SystemLine>
      );
    default:
      break;
  }

  if (blocked && !showBlocked && !revealBlocked) {
    return (
      <div className="flex items-center gap-3 px-4 py-1 text-sm text-faint">
        <span className="flex w-10 justify-center">
          <EyeOff className="size-4" aria-hidden />
        </span>
        Message from someone you blocked.
        <button type="button" className="text-accent hover:underline" onClick={() => setRevealBlocked(true)}>
          Show
        </button>
      </div>
    );
  }

  const del = (skipConfirm: boolean) => {
    const run = () => http.del<void>(`/channels/${message.channelId}/messages/${message.id}`);
    if (skipConfirm) void attempt(run);
    else ui.confirm({ title: 'Delete message?', body: plainText(message.content, useStore.getState()).slice(0, 200) || 'This message will be permanently deleted.', confirmLabel: 'Delete', danger: true, onConfirm: run });
  };
  const reply = () => {
    ui.setReply(message.channelId, message.id);
    document.querySelector<HTMLTextAreaElement>(`textarea[data-composer="${message.channelId}"]`)?.focus();
  };
  const startThread = () => ui.open({ kind: 'createThread', channelId: message.channelId, messageId: message.id, defaultName: plainText(message.content, useStore.getState()).slice(0, 60) || 'New thread' });
  const pin = () => void attempt(() => (message.pinned ? http.del(`/channels/${message.channelId}/pins/${message.id}`) : http.put(`/channels/${message.channelId}/pins/${message.id}`)));
  const publish = () =>
    ui.confirm({
      title: 'Publish this announcement?',
      body: 'Everyone who can see this channel gets a notification and a mention badge. You can only publish a message once.',
      confirmLabel: 'Publish',
      onConfirm: async () => {
        await http.post(`/channels/${message.channelId}/messages/${message.id}/publish`);
      },
    });

  const menu: MenuEntry[] = realId
    ? [
        ...QUICK_REACTIONS.slice(0, 4).map((e) => ({ label: `React with ${e}`, onSelect: () => void toggleReaction(message, e), hidden: !canReact })),
        { label: 'Reply', icon: Reply, onSelect: reply, separatorBefore: canReact },
        { label: 'Create thread', icon: MessagesSquare, hidden: !canThread || !!message.thread, onSelect: startThread },
        { label: 'Edit message', icon: Pencil, hidden: !own, onSelect: () => ui.setEditing(message.id) },
        { label: message.pinned ? 'Unpin message' : 'Pin message', icon: message.pinned ? PinOff : Pin, hidden: !canPin, onSelect: pin },
        { label: 'Publish', icon: Megaphone, hidden: !canPublish, onSelect: publish },
        { label: 'Copy text', icon: Copy, onSelect: () => void copyText(plainText(message.content, useStore.getState())), separatorBefore: true },
        { label: 'Copy message link', icon: Link, onSelect: () => void copyText(`${publicOrigin()}${location.pathname}?m=${message.id}`) },
        { label: 'Copy message ID', icon: Hash, onSelect: () => void copyText(message.id) },
        { label: 'Report message', icon: Flag, hidden: own, danger: true, separatorBefore: true, onSelect: () => ui.open({ kind: 'report', messageId: message.id, spaceId }) },
        { label: 'Delete message', icon: Trash2, hidden: !own && !canManage, danger: true, onSelect: () => del(false) },
        // Commands the bots in this space offer for a message ("Apps").
        ...appMenuEntries(spaceId, 'message', message.id, message.channelId),
      ]
    : [];

  const cards = message.embeds.filter((e) => e.type === 'rich');
  const previews = message.embeds.filter((e): e is LinkEmbed => e.type !== 'rich');
  // Images shown inside an embed aren't listed again as files.
  const inEmbeds = new Set(cards.flatMap((e) => [e.image?.attachmentId, e.thumbnail?.attachmentId]));
  const files = message.attachments.filter((a) => !inEmbeds.has(a.id));
  const body = (
    <>
      {editing ? (
        <EditBox message={message} onDone={() => ui.setEditing(null)} />
      ) : (
        message.content && (
          <div className={clsx('whitespace-pre-wrap break-words leading-[1.4rem] text-fg', isEmojiOnly(message.content) && 'text-4xl leading-tight', pending && 'opacity-60')}>
            <Markdown content={message.content} ctx={ctx} />
            {message.editedAt && (
              <Tooltip label={formatFull(message.editedAt)}>
                <span className="ml-1 select-none text-[11px] text-faint">(edited)</span>
              </Tooltip>
            )}
          </div>
        )
      )}
      {files.length > 0 && (
        <div className={clsx('mt-1 flex flex-col items-start gap-2', pending && 'opacity-60')}>
          {files.map((a) => (
            <AttachmentView key={a.id} a={a} />
          ))}
        </div>
      )}
      {/* A bot's own embeds always show; the setting only hides link previews. */}
      {cards.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-2">
          {cards.map((e, i) => (
            <RichEmbedView key={i} e={e} ctx={ctx} />
          ))}
        </div>
      )}
      {showEmbeds && previews.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-2">
          {previews.map((e) => (
            <EmbedView key={e.url} e={e} />
          ))}
        </div>
      )}
      {!pending && message.components && message.components.length > 0 && <MessageComponents rows={message.components} channelId={message.channelId} messageId={message.id} />}
      {failed && (
        <div className="mt-1 flex items-center gap-2 text-xs text-danger">
          <CircleAlert className="size-3.5" aria-hidden />
          {message.error ?? 'Not sent.'}
          <button type="button" className="inline-flex items-center gap-1 font-medium hover:underline" onClick={() => void retryPending(message.channelId, message.id)}>
            <RotateCcw className="size-3" /> Retry
          </button>
          <button type="button" className="font-medium hover:underline" onClick={() => actions.dropPending(message.channelId, message.id.slice('pending:'.length))}>
            Delete
          </button>
        </div>
      )}
      {message.reactions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {message.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              disabled={!canReact && !r.me}
              onClick={() => void toggleReaction(message, r.emoji)}
              aria-pressed={r.me}
              aria-label={`${r.emoji} ${r.count} reaction${r.count === 1 ? '' : 's'}${r.me ? ', including yours' : ''}`}
              className={clsx('inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-sm transition-colors', r.me ? 'border-accent bg-accent-soft text-fg' : 'border-transparent bg-active text-muted hover:border-line-strong')}
            >
              <span className="text-base leading-none">{r.emoji}</span>
              <span className="text-xs font-semibold">{r.count}</span>
            </button>
          ))}
          {canReact && (
            <EmojiPicker onPick={(e) => void toggleReaction(message, e)} side="top" align="start">
              <button type="button" aria-label="Add reaction" className="inline-flex h-7 items-center rounded-md bg-active px-2 text-muted opacity-0 transition hover:text-fg group-hover/msg:opacity-100 focus:opacity-100">
                <SmilePlus className="size-4" />
              </button>
            </EmojiPicker>
          )}
        </div>
      )}
      {message.thread && (
        <button type="button" onClick={() => ui.openThread(message.thread!.id)} className="mt-1.5 flex items-center gap-2 rounded-md border border-line bg-sidebar px-3 py-1.5 text-sm hover:border-line-strong">
          <MessagesSquare className="size-4 text-accent" aria-hidden />
          <span className="font-medium text-fg">{thread?.name ?? message.thread.name}</span>
          <span className="text-accent">
            {thread?.messageCount ?? message.thread.messageCount} {(thread?.messageCount ?? message.thread.messageCount) === 1 ? 'message' : 'messages'}
          </span>
          {(thread?.archived ?? message.thread.archived) && <span className="text-xs text-faint">Archived</span>}
        </button>
      )}
    </>
  );

  return (
    <ContextMenu entries={menu}>
      <div
        id={`message-${message.id}`}
        data-message-id={message.id}
        className={clsx(
          'group/msg relative px-4 transition-colors',
          grouped ? 'py-0.5' : compact ? 'mt-1 py-0.5' : 'mt-3 py-0.5',
          mentioned && !own ? 'border-l-2 border-mention-edge bg-mention' : announcement && message.publishedAt ? 'border-l-2 border-accent bg-accent-soft/40' : 'hover:bg-hover/60',
          highlighted && 'bg-accent-soft',
        )}
      >
        {message.replyTo && (
          <div className="mb-0.5 flex min-w-0 items-center gap-1.5 pl-12 text-xs text-muted">
            <CornerUpLeft className="size-3.5 shrink-0 text-faint" aria-hidden />
            {message.replyTo.deleted ? (
              <span className="italic text-faint">Original message was deleted</span>
            ) : (
              <button type="button" className="flex min-w-0 items-center gap-1.5 hover:text-fg" onClick={() => ui.jumpTo(message.channelId, message.replyTo!.id)}>
                <Avatar user={message.replyTo.author} size={16} />
                <span className="font-medium" style={roleColorOf(useStore.getState(), spaceId, message.replyTo.authorId ?? '') ? { color: roleColorOf(useStore.getState(), spaceId, message.replyTo.authorId ?? '')! } : undefined}>
                  {message.replyTo.authorId ? displayNameIn(useStore.getState(), message.replyTo.authorId, spaceId) : 'Deleted User'}
                </span>
                <UserBadge user={message.replyTo.author} className="ml-0" />
                <span className="truncate">{plainText(message.replyTo.content, useStore.getState()) || (message.replyTo.hasAttachments ? 'Click to see attachment' : '')}</span>
              </button>
            )}
          </div>
        )}
        {message.interaction && (
          <div className="mb-0.5 flex min-w-0 items-center gap-1.5 pl-12 text-xs text-muted">
            <CornerUpLeft className="size-3.5 shrink-0 text-faint" aria-hidden />
            {message.interaction.user ? (
              <button type="button" className="flex min-w-0 items-center gap-1.5 hover:text-fg" onClick={(e) => ui.openProfile(message.interaction!.user!.id, spaceId, e.currentTarget)}>
                <Avatar user={message.interaction.user} size={16} />
                <span className="font-medium">{displayNameIn(useStore.getState(), message.interaction.user.id, spaceId)}</span>
              </button>
            ) : (
              <span className="font-medium">Someone</span>
            )}
            <span className="truncate">
              {message.interaction.name ? (
                <>
                  used{' '}
                  <span className="font-medium text-accent">
                    {message.interaction.commandType && message.interaction.commandType !== 'chat' ? '' : '/'}
                    {message.interaction.name}
                  </span>
                </>
              ) : (
                'used a bot’s controls'
              )}
            </span>
          </div>
        )}

        {compact ? (
          <div className="flex gap-2">
            <time className="w-14 shrink-0 pt-0.5 text-right text-[11px] text-faint" dateTime={message.createdAt}>
              {formatTime(message.createdAt)}
            </time>
            <div className="min-w-0 flex-1">
              <button type="button" className="mr-1.5 font-semibold hover:underline" style={roleColor ? { color: roleColor } : undefined} onClick={(e) => author && ui.openProfile(author.id, spaceId, e.currentTarget)}>
                {styledName}
              </button>
              {author?.tag && <SpaceTagBadge tag={author.tag} className="mr-1.5 -translate-y-px" />}
              {body}
            </div>
          </div>
        ) : (
          <div className="flex gap-4">
            <div className="w-10 shrink-0">
              {!grouped ? (
                <button type="button" onClick={(e) => author && ui.openProfile(author.id, spaceId, e.currentTarget)} aria-label={`View ${name}'s profile`} className="mt-0.5">
                  <Avatar user={author} size={40} />
                </button>
              ) : (
                <time className="hidden pt-1 text-right text-[11px] leading-none text-faint group-hover/msg:block" dateTime={message.createdAt}>
                  {formatTime(message.createdAt)}
                </time>
              )}
            </div>
            <div className="min-w-0 flex-1">
              {!grouped && (
                <div className="flex items-baseline gap-2">
                  <button type="button" className="truncate font-semibold hover:underline" style={roleColor ? { color: roleColor } : undefined} onClick={(e) => author && ui.openProfile(author.id, spaceId, e.currentTarget)}>
                    {styledName}
                  </button>
                  {author?.tag && <SpaceTagBadge tag={author.tag} className="-ml-1" />}
                  <Tooltip label={formatFull(message.createdAt)}>
                    <time className="shrink-0 text-xs text-faint" dateTime={message.createdAt}>
                      {formatTimestamp(message.createdAt)}
                    </time>
                  </Tooltip>
                  {message.pinned && <Pin className="size-3 text-faint" aria-label="Pinned" />}
                  {message.publishedAt && (
                    <Tooltip label={`Published ${formatFull(message.publishedAt)}`}>
                      <span className="inline-flex items-center gap-1 self-center rounded bg-accent-soft px-1.5 py-px text-[11px] font-semibold text-accent">
                        <Megaphone className="size-3" aria-hidden /> Published
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
              {body}
            </div>
          </div>
        )}

        {realId && !editing && (
          <div className={clsx('absolute -top-4 right-4 z-10 hidden items-center rounded-md border border-line bg-overlay p-0.5 shadow-pop group-hover/msg:flex', pickerOpen && 'flex')}>
            {canReact &&
              QUICK_REACTIONS.slice(0, 3).map((e) => (
                <button key={e} type="button" aria-label={`React with ${e}`} onClick={() => void toggleReaction(message, e)} className="flex size-8 items-center justify-center rounded text-lg hover:bg-hover">
                  {e}
                </button>
              ))}
            {canReact && (
              <EmojiPicker onPick={(e) => void toggleReaction(message, e)} open={pickerOpen} onOpenChange={setPickerOpen} side="left" align="start">
                <IconButton icon={SmilePlus} label="Add reaction" size="sm" />
              </EmojiPicker>
            )}
            <IconButton icon={Reply} label="Reply" size="sm" onClick={reply} />
            {canPublish && <IconButton icon={Megaphone} label="Publish" size="sm" onClick={publish} />}
            {canThread && !message.thread && <IconButton icon={MessagesSquare} label="Create thread" size="sm" onClick={startThread} />}
            {own && <IconButton icon={Pencil} label="Edit" size="sm" onClick={() => ui.setEditing(message.id)} />}
            {(own || canManage) && <IconButton icon={Trash2} label="Delete (Shift-click skips confirmation)" size="sm" danger onClick={(e) => del(e.shiftKey)} />}
          </div>
        )}
      </div>
    </ContextMenu>
  );
});
